import { game } from '../../Game';
import * as AudioManager from '../../utils/AudioManager';
import * as emitterManager from '../../utils/EmitterManager';
import { HITSTOP_FRAMES, requestHitstop } from '../../utils/Hitstop';
import { SoftBreakable } from '../misc/SoftBreakable';
import { cutRing } from './laserTracking';
import { splitTorsoPart } from './laserHalves';
import { splitHead } from './laserHead';
import { burstRing, spillGuts, startDrip } from './laserGore';
import { splitFruit } from './laserFruit';

const HUMANOID_CUT_PARTS = ['head', 'body', 'belly'];
// The beam runs through the middle of a character's depth: behind its head,
// torso, near limbs and eyes, over its far limbs.
const FRONT_PARTS = ['head', 'body', 'belly', 'shoulder_right', 'arm_right', 'hand_right', 'thigh_right', 'leg_right', 'feet_right', 'eye_left', 'eye_right'];
const PART_DAMAGE = {head: 300, body: 300, belly: 150};

const fruitClass = body => {
	const prefabClass = game.editor.retrieveClassFromBody(body);
	return prefabClass instanceof SoftBreakable && prefabClass.base === body && !prefabClass.destroyed ? prefabClass : null;
};

export const cuttableKind = body => {
	if (!body || body.destroyed || body.depthCut || body.myTexture?.goreCut || !body.mySprite?.data) return null;
	if (body.isHumanoid) {
		if (!HUMANOID_CUT_PARTS.includes(body.mySprite.data.refName)) return null;
		const humanoid = game.editor.retrieveSubClassFromBody(body);
		if (!humanoid || humanoid.invincible || game.tutorialMode || humanoid.isPortalTransitActive?.()) return null;
		return 'humanoid';
	}
	return fruitClass(body) ? 'fruit' : null;
};

// Whether a body's art covers the beam. Cut halves keep their side, so the beam
// shows on the cut faces and the halves that come off pass in front of it.
export const isFrontLayer = body => {
	if (!body.mySprite?.data || body.myTexture?.visible === false) return false;
	if (body.depthCut) return body.depthCut === 'front';
	if (body.isHat || fruitClass(body)) return true;
	const refName = body.mySprite.data.refName;
	if (!FRONT_PARTS.includes(refName)) return false;
	if (body.isHumanoid) return true;
	// The far eye stays on the back half of a cut head.
	const lookup = game.editor.retrieveSubClassFromBody(body)?.lookupObject;
	return lookup?.[refName] === body && !(refName === 'eye_right' && lookup.head?.depthCut);
};

export const partDamage = (body, sweptSamples) => {
	const part = body.mySprite.data.refName;
	return (PART_DAMAGE[part] || 0) * sweptSamples / body.depthCutState.swept.length;
};

const splitHumanoidPart = (humanoid, back) => {
	const part = back.mySprite.data.refName;
	back.depthCut = 'back';
	if (part === 'head') splitHead(humanoid, back);
	else splitTorsoPart(humanoid, back, part);

	if (part === 'head' || part === 'body') {
		humanoid.dealDamage(1000);
		if (humanoid.bleedTimer < 0) humanoid.bleedTimer = 0;
	} else {
		humanoid.dealDamage(PART_DAMAGE[part]);
	}
	humanoid.checkLimbs?.();

	// The whole edge of the cut bursts at once, guts tumble out, and both halves keep dripping.
	const ring = cutRing(back.depthCutState.shapes);
	burstRing('bloodRing', back, ring);
	spillGuts(humanoid, back, part, ring);
	startDrip('bloodDrip', back, ring);
	const front = humanoid.depthCutFronts?.[part];
	if (front && !front.destroyed) {
		emitterManager.playOnceEmitter('gorecloud', front, front.GetPosition());
		startDrip('bloodDrip', front, ring);
	}
	const position = back.GetPosition();
	requestHitstop(part === 'head' || part === 'body' ? HITSTOP_FRAMES.HEAD : HITSTOP_FRAMES.LIMB, position, {victim: humanoid.lookupObject._bodies});
	AudioManager.playSFX('laser_slice', 0.6, 0.9 + Math.random() * 0.2, position);
	AudioManager.playSFX(['bash1', 'bash2', 'bash3', 'bash4'], 0.25, 1.1, position);
};

export const splitCut = body => {
	const kind = cuttableKind(body);
	if (kind === 'humanoid') {
		const humanoid = game.editor.retrieveSubClassFromBody(body);
		if (humanoid) splitHumanoidPart(humanoid, body);
	} else if (kind === 'fruit') {
		const fruit = fruitClass(body);
		// A paint bucket bursts instead of falling apart in halves; a hidden one only bursts on a trigger.
		const {isDestructible, isVisible} = fruit.prefabObject.settings;
		if (!fruit.isPaintBucket) splitFruit(fruit, body);
		else if (isDestructible && isVisible !== false) fruit.queueBreak();
	}
};
